import React from 'react';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SentimentVeryDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentVeryDissatisfiedOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import { formatDistanceToNowStrict, isToday, isYesterday, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Aparência de cada tipo de aviso (ícone e cor), compartilhada pelo menu do sino,
 * pelo toast e pela página de notificações. O texto já vem pronto do backend.
 */
const PRESENTATION = {
  appointment_confirmed: { Icon: EventAvailableOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  appointment_canceled: { Icon: EventBusyOutlinedIcon, color: '#C62828', bg: '#FDECEA' },
  reschedule_requested: { Icon: EventRepeatOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  appointment_rescheduled: { Icon: EventRepeatOutlinedIcon, color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' },
  // Etapas obrigatórias da espera concluídas (AppointmentNotifications::readyToCall)
  appointment_ready_to_call: { Icon: HowToRegOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  appointments_unconfirmed: { Icon: HelpOutlineOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  lead_new: { Icon: PersonAddAltOutlinedIcon, color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' },
  referral_subscribed: { Icon: CardGiftcardOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  referral_credit_applied: { Icon: CardGiftcardOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  patient_self_registered: { Icon: PersonAddAltOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  reservation_converted: { Icon: EventAvailableOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  reservation_pending: { Icon: HourglassEmptyOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  reservation_lapsed: { Icon: EventBusyOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  reservation_auto_released: { Icon: EventBusyOutlinedIcon, color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' },
  whatsapp_disconnected: { Icon: WhatsAppIcon, color: '#C62828', bg: '#FDECEA' },
  fiscal_invoice_issued: { Icon: DescriptionOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  fiscal_invoice_error: { Icon: DescriptionOutlinedIcon, color: '#C62828', bg: '#FDECEA' },
  fiscal_invoice_canceled: { Icon: DescriptionOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  nps_detractor: { Icon: SentimentVeryDissatisfiedOutlinedIcon, color: '#C62828', bg: '#FDECEA' },
  export_ready: { Icon: CloudDownloadOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  export_failed: { Icon: CloudDownloadOutlinedIcon, color: '#C62828', bg: '#FDECEA' },
  product_update: { Icon: AutoAwesomeOutlinedIcon, color: 'var(--color-secondary)', bg: 'var(--color-secondary-light)' },
  feedback_update: { Icon: ForumOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  health_professional_request: { Icon: MedicalServicesOutlinedIcon, color: '#E65100', bg: '#FFF3E0' },
  health_professional_granted: { Icon: MedicalServicesOutlinedIcon, color: '#2E7D32', bg: '#E8F5E9' },
  // Dados de exemplo da conta de teste removidos (DemoDataRemover.php)
  demo_data_removed: { Icon: CleaningServicesOutlinedIcon, color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' },
};

const BILLING = { Icon: ReceiptLongOutlinedIcon, color: 'var(--color-primary)', bg: 'var(--color-blue-light4)' };
const DEFAULT = { Icon: NotificationsNoneOutlinedIcon, color: 'var(--color-text-medium)', bg: '#F1F3F8' };

export const presentationFor = (type) => {
  if (PRESENTATION[type]) return PRESENTATION[type];
  if (type && type.startsWith('billing_')) return BILLING;
  return DEFAULT;
};

/** Ícone redondo do aviso, no tamanho pedido. */
export function NotificationAvatar({ type, size = 36 }) {
  const { Icon, color, bg } = presentationFor(type);
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: bg,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: Math.round(size * 0.55) }} />
    </span>
  );
}

// O backend grava em horário do servidor (America/Sao_Paulo); o navegador interpreta como local
const parseServerDate = (value) => (value ? new Date(String(value).replace(' ', 'T')) : null);

/** "há 5 minutos", "ontem às 17:30", "09/10 às 14:00" */
export const describeWhen = (value) => {
  const date = parseServerDate(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const ageMs = Date.now() - date.getTime();
  if (ageMs < 60 * 1000) return 'agora';
  if (isToday(date)) return `há ${formatDistanceToNowStrict(date, { locale: ptBR })}`;
  if (isYesterday(date)) return `ontem às ${format(date, 'HH:mm')}`;
  return format(date, "dd/MM 'às' HH:mm");
};
